import React, { FC, createRef } from 'react'
import styled from 'styled-components'
import { Box, Checkbox, Dropdown, Grid } from '@ads/front-ds'
import Menu from '@ads/front-core/atoms/Menu'
import { PillCombo } from '@ads/front-core/atoms'
import { TDictionary } from '@ads/front-core/interfaces'
import useDropdown from '@ads/front-core/hooks/useDropdown'
import { useClickOutside } from '@ads/front-core/hooks/useEventsOutside'
import { lng } from '@ads/front-core/locales'

type T = TDictionary['id']

interface IPillSelect {
  label: string
  options: TDictionary[]
  selected?: T[]
  onChange: (selected: T[]) => void
}

export const PillSelect: FC<IPillSelect> = ({
  label,
  options,
  selected,
  onChange,
}) => {
  const buttonRef = createRef<HTMLDivElement>()
  const menuRef = createRef<HTMLDivElement>()

  const selectedLength = selected?.length

  const {
    isOpen,
    direction,
    handleToggleDropdown,
    handleCloseDropdown,
  } = useDropdown({ menuRef })

  useClickOutside([buttonRef, menuRef], handleCloseDropdown)

  const handleSelect = (item: T, checked: boolean) => {
    if (checked) {
      if (!selected?.includes(item)) {
        onChange([...(selected || []), item as T])
      }
      return
    }
    onChange(selected?.filter(i => i !== item))
  }

  const handleClear = () => {
    onChange([])
    handleCloseDropdown()
  }

  const dynamicLabel = !selectedLength
    ? lng('common:all')
    : selectedLength === 1
    ? options[0].value
    : lng('common:different')

  return (
    <DropdownStyled
      isOpen={isOpen}
      direction={direction}
      intent="fitted"
      menu={
        <Menu ref={menuRef} minWidth={300} maxHeight={400}>
          <Grid columns={1} gap="xs" px="s" py="xxs">
            {options.map(option => (
              <Box key={option.id}>
                <Checkbox
                  isChecked={selected?.includes(option.id)}
                  onChange={e =>
                    handleSelect(option.id, e.currentTarget?.checked)
                  }
                >
                  {option.value}
                </Checkbox>
              </Box>
            ))}
          </Grid>
        </Menu>
      }
      toggle={
        <Box ref={buttonRef}>
          <PillCombo
            prefix={label}
            label={dynamicLabel}
            count={selectedLength > 0 ? selectedLength : undefined}
            isOpen={isOpen}
            onClick={handleToggleDropdown}
            onClear={selectedLength > 0 ? handleClear : undefined}
          />
        </Box>
      }
    />
  )
}

const DropdownStyled = styled(Dropdown)`
  align-items: center;

  & > div {
    width: 100%;
  }
`
